'use client';

import React, { useState, useEffect } from 'react';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

interface Plan {
  name: string;
  price: number | null;
  duration: string;
}

interface FormData {
  format: string;
  plan: string;
  date: string;
  time: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  goal: string;
}

const plans: Plan[] = [
  { name: 'Старт — 60 мин', price: 9900, duration: '60 мин' },
  { name: 'Про — 120 мин', price: 18900, duration: '120 мин' },
  { name: 'Аудит — 1 день', price: null, duration: '1 день' },
];

const pad = (n: number) => String(n).padStart(2, '0');
const dtICS = (d: Date) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;

export default function Home() {
  const [formData, setFormData] = useState<FormData>({
    format: '',
    plan: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    goal: '',
  });
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const slots = [];
    const baseDate = new Date(`${formData.date}T10:00:00`);
    for (let h = 10; h <= 18; h++) {
      if (h === 13) continue;
      const time = `${pad(h)}:00`;
      slots.push(time);
    }
    setAvailableSlots(slots);
    setSelectedSlot(null);
    setFormData((prev) => ({ ...prev, time: '' }));
  }, [formData.date]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (formStatus) {
      setFormStatus(null);
    }
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked && formStatus?.includes('согласитесь с обработкой')) {
      setFormStatus(null);
    }
  };

  const handleSlotClick = (slot: string) => {
    setSelectedSlot(slot);
    setFormData((prev) => ({ ...prev, time: slot }));
    if (formStatus) {
      setFormStatus(null);
    }
  };

  const scrollToBook = (planName: string) => {
    const bookingSection = document.getElementById('book');
    if (bookingSection) {
      setFormData((prev) => ({ ...prev, plan: planName }));
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.time) {
      setFormStatus('❌ Пожалуйста, выберите доступный слот.');
      return;
    }

    if (!isConsentChecked) {
      setFormStatus('❌ Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    setSubmitting(true);
    setFormStatus('Отправка...');

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/consultations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...formData,
            formName: 'Консультации',
          }
        }),
      });

      if (response.ok) {
        setFormStatus('✅ Спасибо! Ваша заявка отправлена.');
        setShowModal(true);
        setFormData({
          format: '',
          plan: '',
          date: new Date().toISOString().split('T')[0],
          time: '',
          name: '',
          email: '',
          phone: '',
          company: '',
          goal: '',
        });
        setSelectedSlot(null);
        setIsConsentChecked(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setFormStatus(`❌ Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка сети:', error);
      setFormStatus('❌ Не удалось подключиться к серверу. Проверьте ваше соединение.');
    } finally {
      setSubmitting(false);
    }
  };

  const makeEvent = () => {
    if (!formData.plan || !formData.date || !selectedSlot) return null;

    const [hh, mm] = selectedSlot.split(':').map(Number);
    const start = new Date(`${formData.date}T${pad(hh)}:${pad(mm)}:00`);
    let durMin = 60;
    if (formData.plan.includes('120')) durMin = 120;

    const end = new Date(start.getTime() + durMin * 60 * 1000);
    const location = formData.format.includes('Офлайн') ? 'Офис INTERIM' : 'Zoom (ссылку пришлём)';

    return {
      title: `Консультация INTERIM — ${formData.plan}`,
      desc: `Запись с сайта. Тариф: ${formData.plan}.`,
      start,
      end,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      location,
    };
  };

  const handleGoogleCalendar = () => {
    const ev = makeEvent();
    if (!ev) {
      setFormStatus('❌ Выберите тариф, дату и время.');
      return;
    }
    const toIsoZ = (dt: Date) => dt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&details=${encodeURIComponent(ev.desc)}&location=${encodeURIComponent(ev.location)}&dates=${toIsoZ(ev.start)}/${toIsoZ(ev.end)}&ctz=${encodeURIComponent(ev.tz)}`;
    window.open(url, '_blank');
  };

  const handleDownloadIcs = () => {
    const ev = makeEvent();
    if (!ev) {
      setFormStatus('❌ Выберите тариф, дату и время.');
      return;
    }
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//INTERIM//Consulting//RU
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${Date.now()}@interim.local
DTSTAMP:${dtICS(new Date())}Z
DTSTART:${dtICS(ev.start)}
DTEND:${dtICS(ev.end)}
SUMMARY:${ev.title.replace(/,/g, '\\,').replace(/;/g, '\\;')}
DESCRIPTION:${ev.desc.replace(/,/g, '\\,').replace(/;/g, '\\;')}
LOCATION:${ev.location.replace(/,/g, '\\,').replace(/;/g, '\\;')}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'interim_consult.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <style jsx global>
        {`
          :root{ --accent:#FFC300; --card:rgba(255,255,255,.08); --chip:rgba(255,255,255,.14) }
          .card{background:var(--card); backdrop-filter:blur(8px)}
          .chip{background:var(--chip)}
          .btn{border-radius:.75rem; padding:.7rem 1rem; font-weight:700}
          .btn-accent{background:var(--accent); color:#102a43}
          .btn-ghost{background:rgba(255,255,255,.10)}
          .btn-ghost:hover{background:rgba(255,255,255,.20)}
          .tag{background:rgba(255,255,255,.12); padding:.25rem .6rem; border-radius:999px; font-size:.75rem}
          .input{border-radius:.75rem; padding:.75rem 1rem}
          .slot{border-radius:.75rem; padding:.55rem .8rem; background:rgba(255,255,255,.08); cursor:pointer}
          .slot[aria-disabled="true"]{opacity:.45; pointer-events:none}
          .slot.active{outline:2px solid var(--accent); background:rgba(255,255,255,.14)}
          .modal{position:fixed; inset:0; background:rgba(0,0,0,.55); display:none; align-items:center; justify-content:center; z-index:50}
          .modal.open{display:flex}
        `}
      </style>
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
        <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <a href="#why" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                Когда нужно
              </a>
              <a href="#formats" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                Форматы
              </a>
              <a href="#book" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                Запись
              </a>
              <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                FAQ
              </a>
            </nav>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
              Консультация по рекрутингу и RPO
            </h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Разбираем воронку, KPI/SLA, источники кандидатов, автоматизацию, а также модели аутсорс/аутстафф. Результат — пошаговый план на 2–4 недели.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#book" className="btn btn-accent">
                Записаться
              </a>
              <a href="#formats" className="btn btn-ghost">
                Сравнить форматы
              </a>
            </div>
          </div>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <section id="why" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Когда это нужно</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <article className="card rounded-2xl p-5">
                <h3 className="font-semibold text-xl">Падает конверсия/явка</h3>
                <p className="text-sm text-gray-200 mt-2">
                  Найдем узкие места этапов, перепишем сообщения/скрипты, включим напоминания и слот-менеджмент.
                </p>
              </article>
              <article className="card rounded-2xl p-5">
                <h3 className="font-semibold text-xl">Дорого/долго закрываются</h3>
                <p className="text-sm text-gray-200 mt-2">
                  Снизим CPH и ускорим закрытие вакансий за счёт источников, SLA и RPO-процессов.
                </p>
              </article>
              <article className="card rounded-2xl p-5">
                <h3 className="font-semibold text-xl">Нужен масштаб</h3>
                <p className="text-sm text-gray-200 mt-2">
                  План быстрого масштабирования: роли, скрипты, обучение, контроль качества и дашборды.
                </p>
              </article>
            </div>
          </section>

          <section id="formats" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Форматы и тарифы</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {plans.map((plan) => (
                <article key={plan.name} className="card rounded-2xl p-6 flex flex-col">
                  <div className="text-sm tag w-max">
                    {plan.duration.includes('мин') ? 'Онлайн, Zoom' : 'Офлайн, onsite'}
                  </div>
                  <h3 className="text-xl font-bold mt-2">{plan.name}</h3>
                  <p className="text-sm text-gray-200 mt-2">
                    {plan.duration === '60 мин' && 'Диагностика, гипотезы улучшений, чек-лист внедрения.'}
                    {plan.duration === '120 мин' && 'План на 2–4 недели, шаблоны писем/скриптов, метрики.'}
                    {plan.duration === '1 день' && 'Полная диагностика процессов + дорожная карта внедрения.'}
                  </p>
                  <div className="mt-4 text-3xl font-extrabold">
                    {plan.price ? `${plan.price.toLocaleString('ru-RU')} ₽` : 'по запросу'}
                  </div>
                  <ul className="text-sm mt-3 list-disc ml-5 space-y-1">
                    {plan.duration === '60 мин' && (
                      <>
                        <li>Разбор воронки и KPI</li>
                        <li>Рекомендации по каналам</li>
                        <li>Письмо-резюме на e-mail</li>
                      </>
                    )}
                    {plan.duration === '120 мин' && (
                      <>
                        <li>Все из «Старт» +</li>
                        <li>Шаблоны коммуникаций</li>
                        <li>Дашборд показателей</li>
                      </>
                    )}
                    {plan.duration === '1 день' && (
                      <>
                        <li>Интервью с командой</li>
                        <li>Документ «Что делать»</li>
                        <li>Сессия вопросов/ответов</li>
                      </>
                    )}
                  </ul>
                  <button className="btn btn-accent mt-auto" onClick={() => scrollToBook(plan.name)}>
                    Выбрать
                  </button>
                </article>
              ))}
            </div>
          </section>

          <section id="steps" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Как проходит консультация</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <article className="card rounded-2xl p-5">
                <h3 className="font-semibold">1) Бриф</h3>
                <p className="text-sm text-gray-200 mt-2">Короткая анкета, цели, цифры, роли.</p>
              </article>
              <article className="card rounded-2xl p-5">
                <h3 className="font-semibold">2) Сессия</h3>
                <p className="text-sm text-gray-200 mt-2">Разбор воронки/KPI, гипотезы, план.</p>
              </article>
              <article className="card rounded-2xl p-5">
                <h3 className="font-semibold">3) Результаты</h3>
                <p className="text-sm text-gray-200 mt-2">Письмо с материалами и следующими шагами.</p>
              </article>
            </div>
          </section>

          <section id="book" aria-label="Запись" className="simple-card scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold">Записаться на консультацию</h2>
            <p className="text-sm text-gray-200 mt-1">
              Выберите формат и время, оставьте контакты — мы подтвердим по e-mail.
            </p>

            <form onSubmit={handleSubmit} className="mt-5 grid gap-4 md:grid-cols-3">
              <div className="md:col-span-1">
                <label className="text-xs text-gray-300" htmlFor="format">Формат</label>
                <select
                  id="format"
                  className="input text-gray-900 w-full"
                  required
                  value={formData.format}
                  onChange={handleInputChange}
                >
                  <option value="">Выберите</option>
                  <option>Онлайн (Zoom)</option>
                  <option>Офлайн (офис/он-сайт)</option>
                </select>
              </div>

              <div className="md:col-span-1">
                <label className="text-xs text-gray-300" htmlFor="plan">Тариф</label>
                <select
                  id="plan"
                  className="input text-gray-900 w-full"
                  required
                  value={formData.plan}
                  onChange={handleInputChange}
                >
                  <option value="">Выберите</option>
                  <option>Старт — 60 мин</option>
                  <option>Про — 120 мин</option>
                  <option>Аудит — 1 день</option>
                </select>
              </div>

              <div className="md:col-span-1">
                <label className="text-xs text-gray-300" htmlFor="date">Предпочтительная дата</label>
                <input
                  id="date"
                  type="date"
                  className="input text-gray-900 w-full"
                  required
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </div>

              <div className="md:col-span-3">
                <label className="text-xs text-gray-300">Доступные слоты</label>
                <div id="slots" className="mt-2 flex flex-wrap gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      className={`slot ${selectedSlot === slot ? 'active' : ''}`}
                      onClick={() => handleSlotClick(slot)}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
                <input type="hidden" id="time" value={formData.time} required />
              </div>

              <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                <input
                  id="name"
                  className="input text-gray-900 w-full"
                  placeholder="Имя и фамилия*"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
                <input
                  id="email"
                  type="email"
                  className="input text-gray-900 w-full"
                  placeholder="E-mail*"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                />
                <input
                  id="phone"
                  className="input text-gray-900 w-full"
                  placeholder="Телефон*"
                  pattern="^[0-9+()\\-\s]{6,}$"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                <input
                  id="company"
                  className="input text-gray-900 w-full"
                  placeholder="Компания"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>

              <div className="md:col-span-3">
                <textarea
                  id="goal"
                  className="input text-gray-900 w-full"
                  rows={4}
                  placeholder="Цель/вопросы (кратко)"
                  value={formData.goal}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              <div className="md:col-span-3">
                <CustomCheckbox
                  name="consent"
                  isChecked={isConsentChecked}
                  onChange={handleConsentChange}
                  link="/privacy"
                  textColorClass="text-gray-400"
                  linkColorClass="text-[#FF7C22]"
                />
              </div>

              <div className="md:col-span-3 flex flex-wrap gap-3">
                <button type="submit" className="btn btn-accent" disabled={submitting}>
                  {submitting ? 'Отправка...' : 'Отправить заявку'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={!selectedSlot || submitting}
                  onClick={handleGoogleCalendar}
                >
                  Google Calendar
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={!selectedSlot || submitting}
                  onClick={handleDownloadIcs}
                >
                  Скачать .ics
                </button>
              </div>

              {formStatus && (
                <div className={`md:col-span-3 text-center text-sm ${formStatus.startsWith('❌') ? 'text-red-400' : 'text-green-300'}`}>
                  {formStatus}
                </div>
              )}
            </form>
          </section>

          <section id="faq" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
            <div className="space-y-3">
              <details className="card rounded-xl p-4 hover:scale-100 hover:shadow-none">
                <summary className="cursor-pointer font-semibold">Можно перенести консультацию?</summary>
                <p className="mt-2 text-sm text-gray-200">Да, за 24 часа — без штрафов.</p>
              </details>
              <details className="card rounded-xl p-4 hover:scale-100 hover:shadow-none">
                <summary className="cursor-pointer font-semibold">Какие материалы вы пришлёте?</summary>
                <p className="mt-2 text-sm text-gray-200">
                  Чек-лист и план, при тарифе «Про» — шаблоны сообщений и метрик.
                </p>
              </details>
            </div>
          </section>
        </main>

        <div
          id="modal"
          className={`modal ${showModal ? 'open' : ''}`}
          aria-hidden={!showModal}
          role="dialog"
          aria-label="Заявка отправлена"
        >
          <div className="card rounded-2xl p-6 max-w-xl w-[92%]">
            <h3 className="text-2xl font-bold">Спасибо! Заявка принята.</h3>
            <p className="text-sm text-gray-200 mt-2">
              Мы свяжемся по e-mail для подтверждения и пришлём календарное приглашение.
            </p>
            <div className="mt-4 flex gap-2">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}